import React, { useMemo } from "react";
import { usePatientSmsQuery } from "../../../../../../../api/patient/usePatientSmsQuery";
import { uiNotification } from "../../../../../../../services/UINotificationService";
import { Chat as ChatBox } from "../../../../../../../boxes/Chat";
import { MESSAGE_CHAR_LIMIT } from "./config";
import { useClientQuery } from "../../../../../../../api/queries/useClientQuery";
import { useAppTranslation } from "../../../../../../../i18n/useAppTranslation";
import styles from "./styles.module.scss";
import { useChatMessageAdapter } from "./hooks/useChatMessageAdapter";
import { useSendPatientSmsMutation } from "../../../../../../../api/patient/useSendPatientSmsMutation";
import { extractApiError } from "../../../../../../../utilities/api";
import { findBadWordsIn } from "../../../../../../../utilities/general";

/**
 * @param {{
 *  patientId: number;
 * }} param0
 */
export const Chat = ({ patientId }) => {
  const { tClients: t } = useAppTranslation.Clients();
  const { tCommon } = useAppTranslation.Common();

  const patientQuery = useClientQuery({ clientId: patientId });
  const messageAdapter = useChatMessageAdapter(patientId);

  const smsQuery = usePatientSmsQuery(
    { patientId },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      onError: () => {
        uiNotification.error(t("inbox.error.getMessages"));
      },
    },
  );

  const send = useSendPatientSmsMutation({
    onError: (res) => {
      const message = extractApiError(res);
      if (message === "patient_phone_number_not_found") {
        uiNotification.error(t(`inbox.error.${message}`));
      } else {
        uiNotification.error(message || t("inbox.error.sendMessage"));
      }
    },
  });

  const onSend = (message) => {
    const badWords = findBadWordsIn(message || "");

    if (badWords.length > 0) {
      uiNotification.error(tCommon("error.badWords"));
      return Promise.reject();
    }

    return send.mutateAsync({
      patientId,
      message,
      isFirstMessage: smsQuery.data?.length === 0,
      conversationId: smsQuery.data?.[0]?.id || 0,
    });
  };

  const messages = useMemo(
    () => smsQuery.data?.map(messageAdapter) || [],
    [smsQuery.data, messageAdapter],
  );

  return (
    <ChatBox
      messages={messages}
      onSend={onSend}
      isLoading={patientQuery.isLoading || smsQuery.isLoading}
      isSending={send.isLoading}
      messageCharLimit={MESSAGE_CHAR_LIMIT}
      className={styles.chat}
      cannotSendReason={
        patientQuery.data?.doNotSms ? t("inbox.warning.dndSettings") : ""
      }
    />
  );
};
